import { eventChannel, TakeableChannel } from "@redux-saga/core";
import { createAction, EntityId, PayloadAction } from "@reduxjs/toolkit";
import {
  put,
  take,
  call,
  select,
  fork,
  takeEvery,
  actionChannel,
} from "redux-saga/effects";
import {
  Message,
  messagesActions,
  messagesAPI,
  selectIsAdminAuthenticated,
  selectMessagesStatus,
} from "../features";
import { pushNotification } from "./notifications";

export const sendMessage = createAction<Omit<Message, "id">>(
  "saga/messages/postMessage"
);
function* handlePostMessage(action: PayloadAction<Omit<Message, "id">>) {
  try {
    yield put(messagesActions.setStatus("pending"));
    yield call(messagesAPI.postMessage, action.payload);
    yield put(
      pushNotification({
        text: "Message was succesfully sent",
        type: "success",
      })
    );
    yield put(messagesActions.setStatus("fulfilled"));
  } catch (error) {
    yield put(
      pushNotification({
        text: "Error occured when sending a message. Please try again later.",
        type: "error",
      })
    );
    yield put(messagesActions.setStatus("rejected"));
  }
}

export const deleteMessage = createAction<EntityId>(
  "saga/messages/deleteMessage"
);
function* handleDeleteMessage(action: PayloadAction<EntityId>) {
  try {
    yield put(messagesActions.setStatus("pending"));
    // console.log("calling here");
    yield call(messagesAPI.deleteMessage, action.payload);
    yield put(messagesActions.removeMessage(action.payload));
    yield put(
      pushNotification({
        text: "Message was succesfully deleted.",
        type: "success",
      })
    );
    yield put(messagesActions.setStatus("fulfilled"));
  } catch (error) {
    yield put(
      pushNotification({
        text: "Error occured when deleting a message. Please try again later.",
        type: "error",
      })
    );
  }
  yield put(messagesActions.setStatus("rejected"));
}

function* processSendMessages() {
  while (true) {
    // console.log("running");
    const action: PayloadAction<Omit<Message, "id">> = yield take(
      "saga/messages/postMessage"
    );

    const status: string = yield select(selectMessagesStatus);
    // console.log(`status: ${status}`);
    if (status !== "pending") {
      // console.log("handling messages");
      yield fork(handlePostMessage, action);
    } else {
      // console.log("pending");
      yield put(
        pushNotification({
          type: "warning",
          text: "A background process is still running, please try again later.",
        })
      );
    }
  }
}

export const fetchMessages = createAction("saga/messages/fetchMessages");
function* handleFetchMessages() {
  try {
    const data: Message[] = yield call(messagesAPI.fetchData);
    if (data.length) {
      yield put(messagesActions.setMessages(data));
      // yield put(
      //   pushNotification({ type: "success", text: "Fetched new messages." })
      // );
    }
  } catch (error) {
    yield put(
      pushNotification({
        type: "error",
        text: `Failed to fetch messages. ${error.toString()}`,
      })
    );
  }
}

function* processFetchMessages() {
  const fetchChan: TakeableChannel<void> = yield actionChannel(
    "saga/messages/fetchMessages"
  );
  while (true) {
    yield take(fetchChan);
    const isLoggedIn: boolean = yield select(selectIsAdminAuthenticated);
    if (isLoggedIn) {
      yield fork(handleFetchMessages);
    }
  }
}

const createMessageSyncChannel = (syncEveryMsec: number) =>
  eventChannel((emitter) => {
    const intervalHandle = setInterval(() => {
      emitter("sync");
    }, syncEveryMsec);
    return () => {
      clearInterval(intervalHandle);
      emitter("END");
    };
  });

function* backgroundSyncMessages() {
  const evtChan = createMessageSyncChannel(15000);
  while (true) {
    yield take(evtChan);

    yield put(fetchMessages());
  }
}

export function* processMessages() {
  yield takeEvery("saga/messages/deleteMessage", handleDeleteMessage);
  yield fork(processSendMessages);
  yield fork(processFetchMessages);
  yield fork(backgroundSyncMessages);
}
